/**
 * RosterAdmin.js — Vendor roster management page.
 * Route: /roster-admin
 *
 * One screen. Add or edit vetted 1099 trade contractors on the fly.
 * License number is the primary key — same license = update, new = insert.
 */
import React, { useEffect, useState, useCallback } from "react";

const BACKEND_URL = "";
const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";
const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(16px,3vw,32px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };
const btn = { background: CYAN, color: NAVY, border: 0, padding: "10px 20px", fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 13, letterSpacing: "0.03em" };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "10px 12px", borderRadius: 6, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

const TRADE_LANES = [
  "master_electrician_gfci_rewire",
  "licensed_plumbing",
  "hvac_ventilation",
  "structural_balcony_guardrail_welding",
  "ada_mobility_contractor",
  "general_trade",
];

const blank = {
  vendor_name: "", trade_lane: "licensed_plumbing", license_number: "",
  license_expiration: "", insurance_carrier: "", insurance_expiration: "",
  contact_email: "", contact_phone: "", primary_zip: "",
  service_radius_miles: 40, avg_response_hours: 8, verified_by: "Moovin Forward Ops",
};

export default function RosterAdmin() {
  const [roster, setRoster] = useState([]);
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/vendor-discovery/roster`);
    const d = await r.json();
    setRoster(d.vendors || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsert = async () => {
    setStatus(null);
    const r = await fetch(`${BACKEND_URL}/api/vendor-roster/upsert`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        service_radius_miles: +form.service_radius_miles || 0,
        avg_response_hours: +form.avg_response_hours || 0,
      }),
    });
    const d = await r.json();
    if (r.ok) {
      setStatus(`✓ ${d.inserted ? "Added" : "Updated"} ${form.vendor_name}`);
      setForm(blank); load();
    } else { setStatus(`✗ ${d.detail || "Failed"}`); }
  };

  const remove = async (license_number) => {
    if (!window.confirm(`Remove ${license_number}?`)) return;
    await fetch(`${BACKEND_URL}/api/vendor-roster/${license_number}`, { method: "DELETE" });
    load();
  };

  const edit = (v) => setForm({ ...blank, ...v });

  return (
    <div style={shell} data-testid="roster-admin-shell">
      <div style={{ ...card, borderColor: `${CYAN}44` }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · VENDOR ROSTER ADMIN
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 3vw, 26px)" }}>Add + edit vetted 1099 trades</h1>
        <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
          License number is the primary key. Same license = update. New license = insert. Solicitations dispatch to top-3 fastest vendors per trade lane.
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>
          {form.license_number && roster.find(r => r.license_number === form.license_number) ? "EDITING · " + form.vendor_name : "ADD VENDOR"}
        </h3>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
          {[
            ["vendor_name", "Vendor Legal Name *", "text"],
            ["license_number", "TX License Number *", "text"],
            ["license_expiration", "License Expiration", "date"],
            ["insurance_carrier", "Insurance Carrier", "text"],
            ["insurance_expiration", "Insurance Expiration", "date"],
            ["contact_email", "Contact Email", "email"],
            ["contact_phone", "Contact Phone (+1…)", "tel"],
            ["primary_zip", "Primary ZIP", "text"],
            ["service_radius_miles", "Service Radius (miles)", "number"],
            ["avg_response_hours", "Avg Response (hours)", "number"],
          ].map(([k, label, type]) => (
            <label key={k}>
              <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>{label}</div>
              <input type={type} style={input} value={form[k] || ""}
                onChange={e => setForm({ ...form, [k]: e.target.value })}
                data-testid={`roster-input-${k}`} />
            </label>
          ))}
          <label>
            <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Trade Lane *</div>
            <select style={input} value={form.trade_lane} onChange={e => setForm({ ...form, trade_lane: e.target.value })} data-testid="roster-input-trade_lane">
              {TRADE_LANES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button style={btn} onClick={upsert} data-testid="roster-save-btn">💾 Save Vendor</button>
          <button style={{ ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` }}
            onClick={() => setForm(blank)} data-testid="roster-clear-btn">Clear Form</button>
        </div>
        {status && <div style={{ marginTop: 10, fontSize: 13, color: status.startsWith("✓") ? CYAN : "#f04040" }} data-testid="roster-status">{status}</div>}
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>
          CURRENT ROSTER · {roster.length} VENDORS
        </h3>
        {roster.map(v => (
          <div key={v.license_number} style={{ padding: "10px 0", borderTop: `1px solid ${CHROME}22`, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }} data-testid={`roster-row-${v.license_number}`}>
            <div style={{ fontSize: 13 }}>
              <strong>{v.vendor_name}</strong> · {v.trade_lane}<br />
              <span style={{ color: CHROME, fontSize: 11 }}>
                {v.license_number} · {v.contact_email} · {v.contact_phone} · {v.avg_response_hours}h avg response
              </span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => edit(v)} style={{ ...btn, padding: "6px 12px", fontSize: 11 }} data-testid={`roster-edit-${v.license_number}`}>Edit</button>
              <button onClick={() => remove(v.license_number)} style={{ ...btn, padding: "6px 12px", fontSize: 11, background: "transparent", color: "#f04040", border: "1px solid #f0404055" }} data-testid={`roster-delete-${v.license_number}`}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
